import {computed} from 'vue';
import {useStorage} from '@vueuse/core';

import {ContextCompany} from "./types";
import {isAuth} from "./authState";
import {useCompaniesQuery} from "./queries";

/**
 * Выбор компании — клиентское состояние: переживает перезагрузку и синхронизируется между
 * вкладками. Синглтон на уровне модуля (как seenFeatures в useFeatureAnnouncement):
 * useStorage не кеширует ref по ключу, а выбор должен быть один на приложение.
 */
const selectedCompanyBin = useStorage<string | null>('selectedCompanyBin', null);

/**
 * Список компаний из query + выбранная компания поверх него.
 *
 * Composable намеренно без побочных эффектов, поэтому его можно звать из любого числа мест:
 * запрос дедуплицируется по ключу, а в localStorage пишет только явный selectCompany.
 * Протухший bin (компанию открепили) просто не находится в списке и разрешается фолбэком.
 */
export function useSelectedCompany() {
    const {data, isFetching, refetch} = useCompaniesQuery();

    // Гард по isAuth гасит данные сразу на logout, не дожидаясь очистки кэша
    const companies = computed<ContextCompany[]>(() => isAuth.value ? (data.value ?? []) : []);

    // Порядок фолбэков: явный выбор → главная компания → первая из списка
    const selectedCompany = computed<ContextCompany | null>(() => {
        return companies.value.find(company => company.bin === selectedCompanyBin.value)
            ?? companies.value.find(company => company.isMainCompany)
            ?? companies.value[0]
            ?? null;
    });

    const selectCompany = (bin: string) => {
        selectedCompanyBin.value = bin;
    };

    // cancelRefetch: false — не отменяем уже летящий запрос, иначе получим второй такой же
    const refreshCompanies = async (bin?: string) => {
        if (bin) {
            selectCompany(bin);
        }
        await refetch({cancelRefetch: false});
    };

    const resetSelection = () => {
        selectedCompanyBin.value = null;
    };

    return {
        companies,
        selectedCompany,
        selectCompany,
        refreshCompanies,
        resetSelection,
        isCompaniesFetching: isFetching,
    };
}
